import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AI_PERSONAS, MAP_LIST, type MapId, type Persona } from '@cc/shared';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { MiniBoard } from '../components/HexBoard';
import { Logo } from '../components/Logo';
import { useGameStore } from '../store/gameStore';
import { api } from '../net/api';
import { useToast } from '../components/Toast';

type SetupMode = 'local' | 'ai' | 'online';

export function Setup() {
  const { mode: rawMode } = useParams();
  const mode = rawMode as SetupMode;
  const navigate = useNavigate();
  const toast = useToast((state) => state.push);
  const [mapId, setMapId] = useState<MapId>('coindor');
  const [name1, setName1] = useState(mode === 'local' ? 'Player 1' : 'Player');
  const [name2, setName2] = useState('Player 2');
  const [difficulty, setDifficulty] = useState<Persona>('penny');
  const [timerSeconds, setTimerSeconds] = useState<0 | 60 | 120>(0);
  const [busy, setBusy] = useState(false);
  const valid = name1.trim().length > 0 && name1.trim().length <= 14 && (mode !== 'local' || (name2.trim().length > 0 && name2.trim().length <= 14));
  const regionSizes = useMemo(() => Object.fromEntries(MAP_LIST.map((map) => [map.id, map.regions.map((region) => map.spaces.filter((space) => space.regionId === region.id).length).sort((a, b) => b - a).join('·')])), []);
  if (!['local', 'ai', 'online'].includes(mode)) return <Navigate to="/" replace />;
  const start = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      if (mode === 'online') {
        const room = await api.createRoom({ name: name1.trim(), mapId, timerSeconds });
        localStorage.setItem(`cc:room:${room.code}`, room.token);
        navigate(`/room/${room.code}`);
      } else if (mode === 'ai') {
        const aiName = AI_PERSONAS[difficulty].name;
        useGameStore.getState().start({ mode: 'ai', mapId, names: [name1.trim(), aiName], difficulty, humanOriginalIndex: 0 });
        navigate('/play/ai');
      } else {
        useGameStore.getState().start({ mode: 'local', mapId, names: [name1.trim(), name2.trim()] });
        navigate('/play/local');
      }
    } catch (error) { toast(error instanceof Error ? error.message : 'Could not start the game.'); }
    finally { setBusy(false); }
  };
  return (
    <main className="setup-page page-shell wide-shell">
      <header className="page-header"><Link to="/" aria-label="Back home">‹</Link><Logo compact /><span /></header>
      <h1>Set up your game</h1>
      <section><h2>1. Pick a map</h2><div className="map-grid">{MAP_LIST.map((map) => <button key={map.id} className={`map-card card ${mapId === map.id ? 'is-selected' : ''}`} onClick={() => setMapId(map.id)}><MiniBoard mapId={map.id} /><div><h3>{map.name}</h3><p>{map.tagline}</p><span className="region-size">Regions: {regionSizes[map.id]}</span></div></button>)}</div></section>
      <section><h2>2. {mode === 'local' ? 'Players' : 'Your challenger'}</h2><Card className="setup-fields"><label>Your name<input maxLength={14} value={name1} onChange={(event) => setName1(event.target.value)} /></label>{mode === 'local' && <label>Second player<input maxLength={14} value={name2} onChange={(event) => setName2(event.target.value)} /></label>}{mode === 'ai' && <div><span className="field-label">Difficulty</span><div className="difficulty-grid">{(Object.keys(AI_PERSONAS) as Persona[]).map((persona) => <button key={persona} className={`difficulty-card ${difficulty === persona ? 'is-selected' : ''}`} onClick={() => setDifficulty(persona)}><span className={`mascot mascot--${persona}`}>{persona === 'penny' ? '☺' : persona === 'nick' ? '●' : '◆'}</span><strong>{AI_PERSONAS[persona].name}</strong><small>{AI_PERSONAS[persona].label} — {AI_PERSONAS[persona].blurb}</small></button>)}</div></div>}{mode === 'online' && <div><span className="field-label">Turn timer</span><div className="segmented">{([0, 60, 120] as const).map((seconds) => <button key={seconds} className={timerSeconds === seconds ? 'is-selected' : ''} onClick={() => setTimerSeconds(seconds)}>{seconds === 0 ? 'Off' : `${seconds}s`}</button>)}</div><p className="privacy-note">No account, cookies, or personal information beyond your display name.</p></div>}</Card></section>
      <Card className="reminder-strip">New here? <Link to="/rules">Learn how to play</Link></Card>
      <div className="sticky-start"><Button block disabled={!valid || busy} onClick={() => void start()}>{busy ? 'Starting…' : mode === 'online' ? 'Create room' : 'Start game'}</Button></div>
    </main>
  );
}
